import { EntityType } from './entity-type.enum';
import { SystemCheckSums } from './index-db-keys';

export enum ExecutionStatus {
    success,
    failure,
    warning,
}

export interface GenericError {
    message: string;
    caption: string;
    errorNumber: number;
}

export interface Warning {
    warningNumber: number;
    message: string;
    manualOverride: boolean;
    AvailableOptions: Array<[string, string]>;
    OverrideOption: [string, string];
}

export interface ExecutionResult {
    result: {
        status: ExecutionStatus;
        returnXml: string;
        errors: GenericError[];
        warnings: Warning[];
    };
    intermediateSessionId?: string;
    enrichedUsr?: SysUser;
}

export interface ExecuteQueryResult<T> extends ExecutionResult {
    records: T[],
}

export interface SearchResults<T> extends ExecutionResult {
    searchResults: T[],
}

export class  AvionBusinessEntity {
    AbeName?: string;
    EntityId?: string;
    EntityTypeId?: string;
    DisplayId?: number;
    PublicHospital?: boolean;
    CloneSourceId?: string;
    CreationDate?: Date;
    Creator?: string;
    CreatorDesc?: string;
    ModificationDate?: Date;
    Modifier?: string;
    ModifierDesc?: string;
    Description?: string;
    LegacySystemDescription?: any;
    LegacySystemIdentifier?: any;
    OwnerEntity?: string;
    OwnerEntityId?: string;
    OwnerEntityTypeId?: string;
    OwnerDescription?: any;
}

export class SysUser extends AvionBusinessEntity {
    __type?: string;
    UserName?: string;
    Password?: string;
    FirstName?: string;
    LastName?: string;
    WindowsAccount?: string;
    EmailAddress?: string;
    SessionId?: string = '';
    SiteId?: string;
    WebUserId?: string;
    IsSystem?: boolean;

    constructor() {
        super();
        this.EntityId = EntityType.emptyEntityId;
        this.EntityTypeId = EntityType.sysUser;
    }
}

export class SysGroup extends AvionBusinessEntity {
    Groupname!: string;
    constructor() {
        super();
        this.EntityId = EntityType.emptyEntityId;
        this.EntityTypeId = EntityType.sysGroup;
    }
}

export class Usr {
    __type?: string;
    UserName: string = '';
    SessionId?: string = '';
    EntityId: string = '';
    EntityTypeId: string = '';
    SiteId?: string;
    DisplayId?: number | string = '';
    Description?: string = '';

    constructor(usr: SysUser) {
        this.__type = 'SysUser:#Avion.Genesis.Avalon.Core';
    }
}

export class Individual extends AvionBusinessEntity {
    ReciprocalId?: number;
    SubType?: string;
    Extra2: string = '';
    MiddleName: string = '';
    Aboriginal?: boolean;
    PostGradQualsOther: string = '';
    StatementOfAccountEmailCC: string = '';
    RACSID: string = '';
    DefaultPwd: string = '';
    OrganisationName: string = '';
    OrganisationType: string = '';
    MBBSGraduationDate?: Date;
    Title?: string;
    Webuserid?: string;
    TitleName: string = '';
    EmailAddress: string = '';
    Citizenship?: string;
    associations: string = '';
    GraduateInstitute: string = '';
    Gender?: string;
    GenderName?: string;
    Spouse: string = '';
    TorresStraitIslander?: boolean;
    TrainingCompletionYear?: number;
    Qualifications: string = '';
    OTPMode: string = '';
    Status?: string;
    Metro?: boolean;
    JobTitle: string = '';
    SocietyMemberships: string = '';
    PracticingStatus?: string;
    awards: string = '';
    DateDeceased?: Date;
    SourceCode: string = '';
    Branch?: string;
    Extra1: string = '';
    Languages: string = '';
    Hospitals: string = '';
    StatementOfAccountEmail: string = '';
    WebUserId: string = '';
    PostNoms: string = '';
    TFN!: string;
    ABN!: string;
    OperatingStatus?: string;
    FirstName: string = '';
    RelatedEntities?: string;
    Extra4: string = '';
    BirthDate?: Date;
    OTPTarget: string = '';
    LatestMembershipInvoice?: string;
    associateyear?: number;
    LastName: string = '';
    PostGradQuals: string = '';
    SubspecialityAreas: string = '';
    Type?: string;
    FellowYear?: number;
    CurrentStatus!: string;
    Extra3: string = '';
    overseas?: boolean;
    StateOfTraining: string = '';
    RegistrationNumber: string = '';
    PreferredName: string = '';
    __type: string;

    constructor() {
        super();
        this.EntityTypeId = EntityType.individual;
        this.EntityId = EntityType.emptyEntityId;
        this.__type = 'individual:#Avion.Genesis.Avalon.Core';
    }
}

export class UserInfo {
    userId!: string;
    firstName!: string;
    lastName!: string;
    displayName!: string;
    email!: string;
    username!: string;
    clientId!: string;
    clientName!: string;
    clientAcronym!: string;
    orgId!: string;
    accessibleProjects!: string;
    userType!: string;
}

export class SurveyRun extends AvionBusinessEntity {
    allow_multiple_submissions!: boolean;
    completion_text?: string;
    details!: string;
    end_time?: Date;
    execution_code!: string;
    MaxResponses?: number;
    MinRespondentsPercentage?: number;
    qr_code_path?: string;
    response_rate_percent?: number;
    start_time!: Date;
    total_responses?: number;
    Survey!: string;
    SurveyEntity!: string | null;
    Status!: string;
    PercentageScore!: number;
    CurrentProgressLocation!: number;
    constructor() {
        super();
        this.EntityTypeId = EntityType.surveyRun;
        this.EntityId = EntityType.emptyEntityId;
    }
}

export class Survey extends AvionBusinessEntity {
    code!: string;
    completion_text?: string;
    details?: string;
    MinRespondentsPercentage?: number;
    name!: string;
    questions?: SurveyQuestion[] = [];
    RelatedEntities!: string;

    constructor() {
        super();
        this.EntityTypeId = EntityType.survey;
        this.EntityId = EntityType.emptyEntityId;
    }
}

export class SurveyQuestion extends AvionBusinessEntity {
    Category?: string;
    IsOptional?: boolean;
    JumpToQuestion?: string;
    position!: number;
    question_text?: string;
    QuestionTitle?: string;
    response_max_length?: number;
    response_range_max?: number;
    response_range_min?: number;
    response_type!: string;
    ResponseCriteria?: string;
    responses: QuestionResponse[] = [];
    selectedResponseValue: any;
    questionType!: any;
    isComplete = false;

    constructor() {
        super();
        this.EntityTypeId = EntityType.surveyQuestion;
        this.EntityId = EntityType.emptyEntityId;
    }
}

export class ResponseHeader extends AvionBusinessEntity {
    device_desc!: string;
    device_id!: string;
    device_os!: string;
    end_time?: Date;
    ip_address!: string;
    ProgrammeOfStudy?: string;
    start_time!: Date;
    responses: Responses[] = [];

    constructor() {
        super();
        this.EntityTypeId = EntityType.responseHeader;
        this.EntityId = EntityType.emptyEntityId;
    }
}

export class Responses extends AvionBusinessEntity {
    question_id?: string;
    response_id?: string;
    response_value?: string;

    constructor() {
        super();
        this.EntityTypeId = EntityType.response;
        this.EntityId = EntityType.emptyEntityId;
    }
}

export class QuestionResponse extends AvionBusinessEntity {
    IsValid?: boolean;
    JumpToQuestion?: string;
    position?: number;
    response_text?: string;
    isSelected = false;

    constructor() {
        super();
        this.EntityTypeId = EntityType.questionResponse;
        this.EntityId = '00000000-0000-0000-0000-000000000000';
    }
}

export enum QuestionType {
    MultipleChoice,
    SingleChoice,
    FreeText,
    Information
}

export interface ListResult<T> extends ExecutionResult {
    abeList: T[];
}

export interface SystemCheckSumsResult {
    result: {
        status: ExecutionStatus;
        returnXml: string;
        errors: GenericError[];
        warnings: Warning[];
    };
    systemCheckSums: SystemCheckSums[];
}
